/**
 * NursePreviewDialog — pick a client, then open the form exactly as the RN will
 * see it (auto-filled from that client's record) using the editor's unsaved fields.
 */
import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./ui/dialog";
import { Input } from "./ui/input";
import { Button } from "./ui/button";
import { Loader2, Search, Eye } from "lucide-react";
import FillableFormFiller from "./FillableFormFiller";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function NursePreviewDialog({ onClose, previewEndpoint, pdfEndpoint, fields, pageCount, pageSizes, title }) {
  const [clients, setClients] = useState([]);
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [preview, setPreview] = useState(null); // {form, clientName}

  useEffect(() => {
    axios.get(`${API}/clients`, { params: { limit: 500 } })
      .then((r) => setClients(Array.isArray(r.data) ? r.data : r.data.clients || []))
      .catch(() => toast.error("Could not load clients"))
      .finally(() => setLoading(false));
  }, []);

  const shown = clients
    .filter((c) => `${c.first_name} ${c.last_name}`.toLowerCase().includes(q.trim().toLowerCase()))
    .slice(0, 40);

  const openPreview = async (client) => {
    setBusy(true);
    try {
      const r = await axios.post(previewEndpoint, { client_id: client.id, fields });
      setPreview({
        clientName: r.data.client_name,
        form: {
          id: "preview",
          filename: title,
          fillable_form: { enabled: true, fields: fields.map((f) => ({ ...f, assignee: "employee" })), page_count: pageCount, page_sizes: pageSizes },
          default_values: r.data.values || {},
          auto_filled: r.data.auto_filled || [],
        },
      });
    } catch (err) {
      toast.error(err.response?.data?.detail || "Preview failed");
    } finally {
      setBusy(false);
    }
  };

  if (preview) {
    return (
      <FillableFormFiller
        open
        onClose={onClose}
        form={preview.form}
        title={`Preview as nurse — ${preview.clientName}`}
        pdfEndpoint={pdfEndpoint}
        previewMode
        onSubmitValues={async () => false}
      />
    );
  }

  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-md" data-testid="nurse-preview-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-base">
            <Eye className="w-4 h-4 text-sky-600" /> Preview as nurse
          </DialogTitle>
        </DialogHeader>
        <p className="text-xs text-slate-600">Pick a client to see this form the way the RN will — with that client's information filled in from their record. Nothing is saved.</p>
        <div className="relative">
          <Search className="w-3.5 h-3.5 absolute left-2 top-2.5 text-slate-400" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search clients…" className="pl-7 h-8 text-sm" autoFocus data-testid="preview-client-search" />
        </div>
        <div className="max-h-72 overflow-y-auto divide-y rounded border">
          {loading ? (
            <div className="p-4 flex justify-center"><Loader2 className="w-4 h-4 animate-spin text-slate-400" /></div>
          ) : shown.length === 0 ? (
            <p className="p-3 text-xs text-slate-500 italic">No clients match.</p>
          ) : shown.map((c) => (
            <button
              key={c.id}
              onClick={() => openPreview(c)}
              disabled={busy}
              className="w-full text-left px-3 py-2 text-sm hover:bg-sky-50 flex items-center justify-between gap-2"
              data-testid={`preview-client-${c.id}`}
            >
              <span className="truncate">{c.first_name} {c.last_name}</span>
              <span className="text-[10px] text-slate-400 capitalize shrink-0">{(c.status || "").replace(/_/g, " ")}</span>
            </button>
          ))}
        </div>
        <div className="flex justify-end">
          <Button size="sm" variant="ghost" onClick={onClose}>Cancel</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
